// 設定選單 — 對照 Unity SlotFramework `BottomBarView.prefab` 的 `Buttons/Menu/MenuWindow`
// 與 `MenuWindowManager.cs`。
//
// ⚠️ 這**不是彈窗**：Unity 版沒有全螢幕 Mask／暗底、沒有 CanvasGroup、沒有 Animator 也沒有 DOTween，
//    只是掛在設定鈕（btn_set）底下、由 `_childObjectToggle.SetActive(!activeSelf)` 瞬間顯示／隱藏的浮層。
//    先前本移植做成置中的 ModalDialog（暗底＋標題＋三列文字鈕）與 Unity 完全不同，故整個換掉。
//
// 版面（Unity 設計 px，`Bg` 節點）：
//   底圖 pic_systemLlistBg（原生 92×240，spriteBorder left 0 / bottom 65 / right 0 / top 45 ⇒ 只縱向延展）
//   sizeDelta {92, 0} + ContentSizeFitter(Vertical=Preferred) ⇒ 寬固定 92、高由內容撐開
//   pivot (0.5, 0)、anchoredPosition ≈ (0,0) 相對 btn_set 中心 ⇒ **底邊貼齊設定鈕中心、往上生長**
//   VerticalLayoutGroup：padding 上 20 / 下 50、spacing 5、childAlignment UpperCenter
//   子鈕（皆 84×84）：BtnHome → BtnLog → BtnVolume（另有 BtnAuto 但 prefab 內 m_IsActive: 0，未使用）
//   ⇒ 內容高 = 20 + 84×3 + 5×2 + 50 = 332
//
// 音量為**換圖**（`_volumeOnSprite` btn_volume ↔ `_volumeOffSprite` btn_volumeNo），非著色；
// Unity 以「目前 sprite 是否等於 off 圖」判斷靜音狀態，無額外狀態變數。
//
// 自動收合（Unity 以 OnGameEvent 監聽後呼叫 `OnMenuClose()`）：Spin／Auto／Bet／開關資訊頁，
// 以及 Home／Log 被點（會發 UIAllCloseEvent）。**按音量鈕不收合**（只發 VolumeButtonClickEvent）。
//
// [簡化] Home／Log 在 Unity 依平台 URL（HomeUrl／RecordUrl）開新頁；本專案無此環境故為佔位（記 log）。
//        Unity 亦會在 URL 為空時直接 SetActive(false) 隱藏該鈕，本移植保留按鈕以免選單空掉。

import { Container, NineSliceSprite } from 'pixi.js'
import type { EventBus } from '../../../framework/events/EventBus'
import type { GameEventMap } from '../../events/GameEvents'
import { logger } from '../../../framework/core/Logger'
import type { Orientation, Resizable } from '../../../framework/render/design'
import { getUiTexture } from '../../assets/UiTextures'
import { IconButton } from './IconButton'

/** Unity 設計 px（見檔頭）。 */
const BG_W = 92
const ICON = 84
const PAD_TOP = 20
const PAD_BOTTOM = 50
const SPACING = 5
const BG_H = PAD_TOP + ICON * 3 + SPACING * 2 + PAD_BOTTOM
/** pic_systemLlistBg 的 spriteBorder（left 0 / bottom 65 / right 0 / top 45）。 */
const SLICE_TOP = 45
const SLICE_BOTTOM = 65

/** 設定鈕的螢幕位置與顯示邊長（由 BottomBarView.menuButtonAnchor 提供）。 */
export type MenuAnchor = () => { readonly x: number; readonly y: number; readonly size: number }

export class MenuPopup extends Container implements Resizable {
  private readonly bg = new NineSliceSprite({
    texture: getUiTexture('menuListBg'),
    leftWidth: 0,
    rightWidth: 0,
    topHeight: SLICE_TOP,
    bottomHeight: SLICE_BOTTOM,
  })

  private readonly homeBtn = new IconButton(getUiTexture('iconHome'), () => this.onHome())
  private readonly logBtn = new IconButton(getUiTexture('iconLog'), () => this.onLog())
  private readonly volumeBtn = new IconButton(getUiTexture('iconVolume'), () => this.toggleMute())

  private muted = false

  constructor(
    private readonly bus: EventBus<GameEventMap>,
    private readonly anchor: MenuAnchor,
  ) {
    super()
    this.visible = false
    this.addChild(this.bg, this.homeBtn, this.logBtn, this.volumeBtn)

    this.bus.on('menuClicked', () => this.toggle())
    // 自動收合（對應 Unity 的 SpinTriggerEvent／UIAutoClickEvent／UIBetClickEvent／InfoUIActiveEvent）。
    for (const event of ['spinRequested', 'autoSpinClicked', 'betMenuClicked', 'infoClicked'] as const) {
      this.bus.on(event, () => this.close())
    }
  }

  private toggle(): void {
    this.visible = !this.visible
    if (this.visible) this.layout()
  }

  private close(): void {
    this.visible = false
  }

  private onHome(): void {
    logger.info('返回大廳 — 需平台 HomeUrl，本專案無此環境（佔位）')
    this.close()
  }

  private onLog(): void {
    logger.info('遊戲紀錄 — 需平台 RecordUrl，本專案無此環境（佔位）')
    this.close()
  }

  /** 全域靜音切換：換圖並廣播（對應 Unity OnVolumeClick 的 MuteMusic+MuteSFX）；選單不收合。 */
  private toggleMute(): void {
    this.muted = !this.muted
    this.volumeBtn.setTexture(getUiTexture(this.muted ? 'iconVolumeOff' : 'iconVolume'))
    this.bus.emit('muteToggled', { muted: this.muted })
  }

  resize(_width: number, _height: number, _orientation: Orientation): void {
    this.layout()
  }

  /**
   * 依設定鈕位置排版：底邊貼齊鈕中心往上長。
   * 縮放取自設定鈕的實際邊長（s = size / 84），故橫直版共用同一套 Unity 設計值。
   */
  private layout(): void {
    const { x, y, size } = this.anchor()
    if (size <= 0) return
    const s = size / ICON

    const width = BG_W * s
    const height = BG_H * s
    const top = y - height
    this.bg.width = width
    this.bg.height = height
    this.bg.position.set(x - width / 2, top)

    const buttons = [this.homeBtn, this.logBtn, this.volumeBtn]
    buttons.forEach((button, index) => {
      button.setIconSize(ICON * s)
      button.position.set(x, top + (PAD_TOP + (ICON + SPACING) * index + ICON / 2) * s)
    })
  }
}
